import { useApiClient } from '@m43/core';
import { useQuery } from '@tanstack/react-query';
import {
    UnitEmergencyAckDto,
    UnitEmergencyDto,
    UnitResource,
    UserAssignmentBundle,
} from '@mark43/cad-api';
import { useIntegratedStreams } from '../common/hooks/use-integrated-streams';
import { useBootstrapState } from '../bootstrap/bootstrap-provider';
import { useEffect, useMemo, useState } from 'react';
import { useEnabledFeatures } from '../common/hooks/enabled-features/use-enabled-features';
import { useDispatchAreaStore } from '../dispatch-areas/use-dispatch-areas';
import { useApplicationType } from '../common/hooks/use-application-type';
import { useUnitAssignment } from '../unit-assignment/unit-assignment-context';
import { toBooleanMap } from '../common/helpers/utils';
import { autorun } from 'mobx';
import { useHandleUnitEmergencySoundAlert } from './use-handle-unit-emergency-sound-alert';
import { shouldShowUnitEmergencyInCad, shouldShowUnitEmergencyInFr } from './helpers';

const UNIT_EMERGENCY_BOOTSTRAP_QUERY_KEY = 'unit-emergency-location-updated';

type IntegratedUnitEmergenciesReturn = {
    unAckedUnitEmergencies: UnitEmergencyDto[];
    filteredUnAckedUnitEmergencies: UnitEmergencyDto[];
    addAcknowledgedUnitEmergency: (ackUnitEmergencyDto: UnitEmergencyAckDto) => void;
    ackedUnitEmergencies: UnitEmergencyAckDto[];
    activeUnitEmergency: UnitEmergencyDto | null;
};

const filterUnacknowledgedEmergencies = (
    unacknowledgedUnitEmergencies: UnitEmergencyDto[],
    unitAssignment: UserAssignmentBundle | undefined,
    isFirstResponder: boolean
) => {
    if (unacknowledgedUnitEmergencies.length === 0) {
        return [];
    }
    if (unacknowledgedUnitEmergencies.length > 0 && unitAssignment && isFirstResponder) {
        return unacknowledgedUnitEmergencies.filter((dto) => dto.unitId !== unitAssignment.unitId);
    }
    return unacknowledgedUnitEmergencies;
};

export function useIntegratedUnitEmergencies({
    enabled,
}: {
    enabled: boolean;
}): IntegratedUnitEmergenciesReturn {
    const { isFirstResponder } = useApplicationType();
    const bootstrap = useBootstrapState();
    const dispatchAreaStore = useDispatchAreaStore();
    const unitAssignment = useUnitAssignment();
    const apiClient = useApiClient();
    const { unitEmergencyEnabled } = useEnabledFeatures();
    const [monitoredAndOwnedDispatchAreaIds, setMonitoredAndOwnedDispatchAreaIds] = useState<
        number[]
    >(dispatchAreaStore.monitoredAndOwnedDispatchAreaIds);
    const [unackedUnitEmergencies, setUnackedUnitEmergencies] = useState<UnitEmergencyDto[]>(
        bootstrap.unAckedInProgressUnitEmergencies
    );
    const [ackedUnitEmergencies, setAckedUnitEmergencies] = useState<UnitEmergencyAckDto[]>(
        bootstrap.ackedUnitEmergencies
    );

    useEffect(() => {
        autorun(() => {
            setMonitoredAndOwnedDispatchAreaIds(dispatchAreaStore.monitoredAndOwnedDispatchAreaIds);
        });
    }, [dispatchAreaStore]);

    const acknowledgedUnitEmergenciesMap = useMemo(() => {
        const acknowledgedUnitEmergencyIds = ackedUnitEmergencies.map((dto) => dto.unitEmergencyId);
        return toBooleanMap(acknowledgedUnitEmergencyIds);
    }, [ackedUnitEmergencies]);

    const unackedUnitEmergenciesInRelevantDispatchAreas = useMemo(() => {
        if (isFirstResponder) {
            if (!unitAssignment?.unitDispatchAreaId) {
                return [];
            }

            return unackedUnitEmergencies.filter((unitEmergencyDto) =>
                shouldShowUnitEmergencyInFr({
                    unitEmergencyDto,
                    userAgencyTypeGlobalAttrId: bootstrap.userAgencyTypeGlobalAttrId,
                    unitDispatchAreaId: unitAssignment.unitDispatchAreaId,
                })
            );
        }

        if (monitoredAndOwnedDispatchAreaIds.length === 0) {
            return [];
        }

        return unackedUnitEmergencies.filter((unitEmergencyDto) =>
            shouldShowUnitEmergencyInCad({
                unitEmergencyDto,
                userAgencyTypeGlobalAttrId: bootstrap.userAgencyTypeGlobalAttrId,
                monitoredAndOwnedDispatchAreaIds,
            })
        );
    }, [
        monitoredAndOwnedDispatchAreaIds,
        unackedUnitEmergencies,
        bootstrap.userAgencyTypeGlobalAttrId,
        isFirstResponder,
        unitAssignment,
    ]);

    const filteredUnAckedUnitEmergencies = useMemo(() => {
        return filterUnacknowledgedEmergencies(
            unackedUnitEmergenciesInRelevantDispatchAreas,
            unitAssignment,
            isFirstResponder
        );
    }, [unackedUnitEmergenciesInRelevantDispatchAreas, unitAssignment, isFirstResponder]);

    const [activeUnitEmergency, setActiveUnitEmergency] = useState<UnitEmergencyDto | null>(() => {
        const idx = bootstrap.unAckedInProgressUnitEmergencies.findIndex(
            (dto) => dto.unitId === unitAssignment?.unitId
        );
        if (idx > -1) {
            return bootstrap.unAckedInProgressUnitEmergencies[idx];
        }
        return null;
    });

    const addAcknowledgedUnitEmergency = (acknowledgedUnitEmergency: UnitEmergencyAckDto) => {
        const filteredUnacknowledgedUnitEmergencies = unackedUnitEmergenciesInRelevantDispatchAreas.filter(
            (dto) => dto.id !== acknowledgedUnitEmergency.unitEmergencyId
        );
        setUnackedUnitEmergencies([...filteredUnacknowledgedUnitEmergencies]);
        setAckedUnitEmergencies((acknowledgedUnitEmergencies) => [
            ...acknowledgedUnitEmergencies,
            acknowledgedUnitEmergency,
        ]);
    };

    const handleEndedUnitEmergency = (updatedEmergencyDto: UnitEmergencyDto) => {
        if (activeUnitEmergency && activeUnitEmergency.id === updatedEmergencyDto.id) {
            setActiveUnitEmergency(null);
        }
        setUnackedUnitEmergencies((emergencies) =>
            emergencies.filter((dto) => dto.id !== updatedEmergencyDto.id)
        );
    };

    const handleUpdatedUnitEmergency = (
        updatedUnitEmergencyDto: UnitEmergencyDto
    ): UnitEmergencyDto[] => {
        const unacknowledgedUnitEmergencies = [...unackedUnitEmergencies];
        const updatedUnitEmergencyDtoIdx = unacknowledgedUnitEmergencies.findIndex(
            (dto) => dto.id === updatedUnitEmergencyDto.id
        );
        if (updatedUnitEmergencyDtoIdx > -1) {
            unacknowledgedUnitEmergencies[updatedUnitEmergencyDtoIdx] = {
                ...updatedUnitEmergencyDto,
            };
        } else {
            unacknowledgedUnitEmergencies.push({ ...updatedUnitEmergencyDto });
        }

        return unacknowledgedUnitEmergencies;
    };

    const updateUnitEmergencyDto = (updatedUnitEmergencyDto: UnitEmergencyDto) => {
        const isActiveUnitEmergency = activeUnitEmergency?.id === updatedUnitEmergencyDto.id;
        let shouldSkipUnitEmergenciesUpdate = false;

        if (!isActiveUnitEmergency) {
            shouldSkipUnitEmergenciesUpdate =
                (unackedUnitEmergencies.length === 0 && updatedUnitEmergencyDto.hasEnded) ||
                acknowledgedUnitEmergenciesMap[updatedUnitEmergencyDto.id];
        }

        if (shouldSkipUnitEmergenciesUpdate) {
            return;
        }

        if (
            updatedUnitEmergencyDto.hasEnded &&
            (unackedUnitEmergencies.length > 0 || isActiveUnitEmergency)
        ) {
            handleEndedUnitEmergency(updatedUnitEmergencyDto);
            return;
        }

        // Set the user's active emergency if it is currently inactive
        if (!activeUnitEmergency && updatedUnitEmergencyDto.unitId === unitAssignment?.unitId) {
            setActiveUnitEmergency(updatedUnitEmergencyDto);
        }

        // Unit Assignment only applicable when in FR.
        if (!updatedUnitEmergencyDto.hasEnded && unackedUnitEmergencies.length === 0) {
            setUnackedUnitEmergencies([updatedUnitEmergencyDto]);
            return;
        }
        const unacknowledgedUnitEmergencies = handleUpdatedUnitEmergency(updatedUnitEmergencyDto);
        setUnackedUnitEmergencies(unacknowledgedUnitEmergencies);
    };

    const bootstrapQuery = useQuery({
        queryKey: [UNIT_EMERGENCY_BOOTSTRAP_QUERY_KEY],
        enabled,
        queryFn() {
            if (unitEmergencyEnabled) {
                return apiClient.request<UnitResource.FetchInProgressUnitEmergenciesForDepartment>({
                    method: 'GET',
                    url: 'unit/unit_emergency',
                });
            }
        },
    });

    useHandleUnitEmergencySoundAlert({ filteredUnAckedUnitEmergencies });

    useIntegratedStreams({
        query: bootstrapQuery,
        streamNames: ['unitEmergencyDto'],
        onMessage: (message) => {
            updateUnitEmergencyDto(message.payload);
            return [message.payload];
        },
        enabled,
    });

    return {
        unAckedUnitEmergencies: unackedUnitEmergencies,
        filteredUnAckedUnitEmergencies,
        ackedUnitEmergencies,
        addAcknowledgedUnitEmergency,
        activeUnitEmergency,
    };
}
